import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import Typography from '../Typography';
import FontIcon from '../FontIcon';

export type MessageCardCategory =
  | 'Caution'
  | 'Valid'
  | 'Error'
  | 'Default';

export interface MessageCardProps {
  category?: MessageCardCategory;
  overline?: ReactNode;
  headline?: ReactNode;
  supportiveLabel?: ReactNode;
  description?: ReactNode;
  boldDescription?: ReactNode;
  leadingItem?: ReactNode;
  buttons?: ReactNode;
  showCloseButton?: boolean;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const CardContainer = styled.div<{ $category: MessageCardCategory }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: clip;
  padding: 16px;
  position: relative;
  border-radius: 28px;
  width: auto;
  box-sizing: border-box;
  transition: all 0.2s ease-in-out;
  color: var(--sys-color--on-surface-variant);

  ${({ $category }) => {
    switch ($category) {
      case 'Error':
        return `
          background: var(--sys-color--error-container);
        `;
      case 'Caution':
        return `
          background: var(--sys-color-extended--goldenrod-container);
        `;
      case 'Valid':
        return `
          background: var(--sys-color-extended--green-container);
        `;
      default:
        return `
          background: var(--sys-color--surface-container);
        `;
    }
  }}
`;

const HeaderAndContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const LeadingItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;



const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;

  span, img {
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
  margin-top: 4px;
`;

export const MessageCard: FC<MessageCardProps> = ({
  category = 'Default',
  overline,
  headline,
  supportiveLabel,
  description,
  boldDescription,
  leadingItem,
  buttons,
  showCloseButton = false,
  onClose,
  className,
  style,
}) => {
  // Resolve leading item based on category if not explicitly passed
  const renderLeadingItem = () => {
    if (leadingItem) {
      return <LeadingItemContainer>{leadingItem}</LeadingItemContainer>;
    }

    switch (category) {
      case 'Error':
        return (
          <LeadingItemContainer>
            <FontIcon color="var(--sys-color--error)" name="error" size={24} />
          </LeadingItemContainer>
        );
      case 'Caution':
        return (
          <LeadingItemContainer>
            <FontIcon color="var(--sys-color-extended--goldenrod)" name="error" size={24} />
          </LeadingItemContainer>
        );
      case 'Valid':
        return (
          <LeadingItemContainer>
            <FontIcon name="check_circle" size={24} />
          </LeadingItemContainer>
        );
      default:
        return (
          <LeadingItemContainer>
            <FontIcon name="info" size={24} />
          </LeadingItemContainer>
        );
    }
  };

  const hasHeadingGroup = overline || headline || supportiveLabel;

  return (
    <CardContainer $category={category} className={className} style={style}>
      <HeaderAndContent>
        {renderLeadingItem()}

        <TextGroup>
          {hasHeadingGroup && (
            <HeadingGroup>
              {overline && (
                <Typography variant="bodySmall" color="inherit">
                  {overline}
                </Typography>
              )}
              {headline && (
                <Typography variant="titleMediumEmphasized" color="inherit">
                  {headline}
                </Typography>
              )}
              {supportiveLabel && (
                <Typography variant="bodyMediumEmphasized" color="inherit">
                  {supportiveLabel}
                </Typography>
              )}
            </HeadingGroup>
          )}

          {description && (
            <Typography variant="bodyMedium" color="inherit">
              {description}
            </Typography>
          )}

          {boldDescription && (
            <Typography variant="bodyMediumEmphasized" color="inherit">
              {boldDescription}
            </Typography>
          )}

          {buttons && <ButtonGroup>{buttons}</ButtonGroup>}
        </TextGroup>
      </HeaderAndContent>

      {showCloseButton && (
        <CloseButton onClick={onClose} aria-label="Close">
          <FontIcon name="close" size={20} />
        </CloseButton>
      )}
    </CardContainer>
  );
};

export default MessageCard;
